import {
  getSharedInventoryFilterCapabilities,
  getSharedInventoryFilterMapping,
  normalizeFeedName,
  type SharedInventoryCondition,
  type SharedInventoryFilter,
  type SharedInventoryFilterField,
  type SupportedFeed,
} from '@jazel-auto/ise-contracts'

export interface LocalSharedInventoryFilter {
  condition: SharedInventoryCondition[]
  make: string
  model: string
  yearMin: number | null
  yearMax: number | null
  keyword: string
  accountName: string
}

export const EMPTY_SHARED_INVENTORY_FILTER: LocalSharedInventoryFilter = {
  condition: [],
  make: '',
  model: '',
  yearMin: null,
  yearMax: null,
  keyword: '',
  accountName: '',
}

export function createEmptySharedInventoryFilter(): LocalSharedInventoryFilter {
  return {
    ...EMPTY_SHARED_INVENTORY_FILTER,
    condition: [],
  }
}

export function resolveSharedInventoryFeed(
  feedSource: unknown,
): SupportedFeed | null {
  return normalizeFeedName(feedSource)
}

export function sharedInventoryCapabilities(
  feedSource: unknown,
): readonly SharedInventoryFilterField[] {
  const feed = resolveSharedInventoryFeed(feedSource)
  return feed ? getSharedInventoryFilterCapabilities(feed) : []
}

export function hasSharedInventoryFilterCapabilities(
  feedSource: unknown,
): boolean {
  return sharedInventoryCapabilities(feedSource).length > 0
}

/** Raw keyword columns advertised by the Shared Inventory contract for the selected feed. */
export function sharedInventoryKeywordSourceColumns(
  feedSource: unknown,
): readonly string[] {
  const feed = resolveSharedInventoryFeed(feedSource)
  if (!feed) return []
  return getSharedInventoryFilterMapping(feed).keyword ?? []
}

/** Persistent Keyword field hint; null when Keyword is unsupported for the feed. */
export function sharedInventoryKeywordSearchHint(
  feedSource: unknown,
): string | null {
  const columns = sharedInventoryKeywordSourceColumns(feedSource)
  if (columns.length === 0) return null
  return `Searches feed fields: ${columns.join(', ')}`
}

export function sharedInventoryFilterToApi(
  filter: LocalSharedInventoryFilter,
  feedSource: unknown,
): SharedInventoryFilter {
  const capabilities = new Set(sharedInventoryCapabilities(feedSource))
  const result: SharedInventoryFilter = {}

  if (capabilities.has('condition') && filter.condition.length > 0) {
    result.condition = [...filter.condition]
  }

  for (const field of ['make', 'model', 'keyword', 'accountName'] as const) {
    const value = filter[field].trim()
    if (capabilities.has(field) && value) {
      result[field] = value
    }
  }

  if (
    capabilities.has('yearMin')
    && filter.yearMin !== null
    && Number.isFinite(filter.yearMin)
  ) {
    result.yearMin = filter.yearMin
  }
  if (
    capabilities.has('yearMax')
    && filter.yearMax !== null
    && Number.isFinite(filter.yearMax)
  ) {
    result.yearMax = filter.yearMax
  }

  return result
}

export function sharedInventoryFilterToLocal(
  filter: SharedInventoryFilter | undefined,
  feedSource: unknown,
): LocalSharedInventoryFilter {
  const capabilities = new Set(sharedInventoryCapabilities(feedSource))
  const local = createEmptySharedInventoryFilter()

  if (capabilities.has('condition') && Array.isArray(filter?.condition)) {
    local.condition = filter.condition.filter(
      (condition): condition is SharedInventoryCondition =>
        condition === 'new' || condition === 'used' || condition === 'cpo',
    )
  }
  for (const field of ['make', 'model', 'keyword', 'accountName'] as const) {
    const value = filter?.[field]
    if (capabilities.has(field) && typeof value === 'string') {
      local[field] = value
    }
  }
  if (
    capabilities.has('yearMin')
    && typeof filter?.yearMin === 'number'
    && Number.isFinite(filter.yearMin)
  ) {
    local.yearMin = filter.yearMin
  }
  if (
    capabilities.has('yearMax')
    && typeof filter?.yearMax === 'number'
    && Number.isFinite(filter.yearMax)
  ) {
    local.yearMax = filter.yearMax
  }

  return local
}

export function pruneSharedInventoryFilter(
  filter: LocalSharedInventoryFilter,
  feedSource: unknown,
): LocalSharedInventoryFilter {
  return sharedInventoryFilterToLocal(
    sharedInventoryFilterToApi(filter, feedSource),
    feedSource,
  )
}
